import { useState } from "react";
import { useMountEffect } from "@/lib/useMountEffect";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, X, Edit3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminUsers() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // user being edited, or 'new'

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useMountEffect(load);

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      setUsers((xs) => xs.filter((x) => x.id !== id));
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <AdminLayout
      title="Users"
      subtitle="Admins and staff who can access the Nexra admin platform."
      actions={
        <button onClick={() => setEditing("new")} className="btn-primary" data-testid="users-add-button">
          <Plus className="h-4 w-4" />
          Add user
        </button>
      }
    >
      {error && <div className="mb-4 text-sm text-destructive">{error}</div>}
      <div className="border border-border bg-white overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-obsidian text-white text-left">
            <tr>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Name</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Email</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Role</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em]">Created</th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${
                      u.role === "admin" ? "bg-execution text-white" : "bg-muted text-foreground border border-border"
                    }`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setEditing(u)}
                        className="h-8 w-8 inline-flex items-center justify-center border border-border hover:bg-obsidian hover:text-white hover:border-obsidian transition-colors"
                        data-testid={`user-edit-${u.id}`}
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      {u.id !== current?.id && (
                        <button
                          onClick={() => del(u.id)}
                          className="h-8 w-8 inline-flex items-center justify-center border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                          data-testid={`user-delete-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserModal
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(u) => {
            setUsers((xs) => {
              const i = xs.findIndex((x) => x.id === u.id);
              if (i === -1) return [u, ...xs];
              const cp = [...xs];
              cp[i] = u;
              return cp;
            });
            setEditing(null);
          }}
        />
      )}
    </AdminLayout>
  );
}

function UserModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(initial?.role || "staff");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isNew = !initial;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/users", { name, email, password, role });
        onSaved(data);
      } else {
        const payload = { name, role };
        if (password) payload.password = password;
        const { data } = await api.patch(`/users/${initial.id}`, payload);
        onSaved(data);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-obsidian/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="user-modal">
      <form onSubmit={submit} className="bg-white border border-border w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-semibold">{isNew ? "Add user" : "Edit user"}</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-muted" data-testid="user-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4">
          <Field label="Name">
            <input className="input-swiss" required value={name} onChange={(e) => setName(e.target.value)} data-testid="user-modal-name" />
          </Field>
          <Field label="Email">
            <input
              className="input-swiss"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!isNew}
              data-testid="user-modal-email"
            />
          </Field>
          <Field label={isNew ? "Password" : "New password (optional)"}>
            <input
              className="input-swiss"
              type="password"
              required={isNew}
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="user-modal-password"
            />
          </Field>
          <Field label="Role">
            <select className="input-swiss" value={role} onChange={(e) => setRole(e.target.value)} data-testid="user-modal-role">
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="user-modal-save">
            {saving ? "Saving..." : isNew ? "Create user" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</span>
      {children}
    </label>
  );
}
